import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FieldEntry } from '../hooks';
import FieldRow from './FieldRow';

interface FieldSectionProps {
  title: string;
  fields: FieldEntry[];
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  ref?: React.Ref<HTMLDivElement>;
  beforeFields?: React.ReactNode;
  columns?: number;
  showGroups?: boolean;
}

const FieldSection: React.FC<FieldSectionProps> = ({
  title,
  fields,
  expanded,
  onChange,
  ref,
  beforeFields,
  columns = 1,
  showGroups = true,
}) => {
  const { ungroupedFields, groupedFields } = useMemo(() => {
    const ungrouped: FieldEntry[] = [];
    const grouped: Record<string, FieldEntry[]> = {};

    fields.forEach((f) => {
      if (showGroups && f.group) {
        if (!grouped[f.group]) {
          grouped[f.group] = [];
        }
        grouped[f.group].push(f);
      } else {
        ungrouped.push(f);
      }
    });

    return { ungroupedFields: ungrouped, groupedFields: grouped };
  }, [fields, showGroups]);

  if (fields.length === 0) return null;

  const renderFieldGrid = (items: FieldEntry[], isGrouped = false) => (
    <Grid
      container
      sx={(theme) => ({
        '& > *': {
          borderBottom: columns > 1 ? 'none' : '1px solid',
          borderColor: 'divider',
        },
        '& > *:nth-last-child(1)': {
          borderBottom: 'none',
        },
        [theme.breakpoints.up('md')]: {
          [`& > *:nth-last-child(-n + ${items.length % columns || columns})`]: {
            borderBottom: 'none !important',
          },
        },
        ...(isGrouped && {
          mt: 1,
          mb: 2,
        }),
      })}
    >
      {items.map((f) => (
        <Grid
          key={f.label}
          size={{
            xs: 12,
            md: 12 / columns,
          }}
          sx={{}}
        >
          <FieldRow label={f.label} value={f.value} format={f.format} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Accordion
      ref={ref}
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onChange}
      sx={{
        '&:before': { display: 'none' },
        bgcolor: 'transparent',
        borderBottom: '1px solid',
        borderColor: 'divider',
        my: 1,
        '& .MuiAccordionSummary-root': {
          bgcolor: 'white !important',
        },
        /**
         * Media query for print styles
         * Collapse component is always visible
         */
        '@media print': {
          boxShadow: 'none',
          '& .MuiCollapse-root': {
            display: 'block !important',
            height: 'auto !important',
            visibility: 'visible !important',
          },
          '& .MuiAccordion-root': {
            /* Prevents an accordion from splitting across two pages */
            'break-inside': 'avoid',
            /* Legacy support */
            'page-break-inside': 'avoid',
          },
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          expanded ? (
            <RemoveIcon sx={{ color: 'text.primary' }} />
          ) : (
            <AddIcon sx={{ color: 'text.primary' }} />
          )
        }
        sx={{
          px: 0,
          '& .MuiAccordionSummary-content': { my: 1.5 },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'none',
            '&.Mui-expanded': {
              transform: 'none',
            },
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: 'text.primary',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          py: 0,
          '& > .MuiBox-root:last-child': {
            border: 'none',
          },
        }}
      >
        {beforeFields}
        {ungroupedFields.length > 0 && renderFieldGrid(ungroupedFields)}
        {Object.entries(groupedFields).map(([groupName, groupFields]) => (
          <Box key={groupName} sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                px: 1,
                mb: 1,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {groupName}
            </Typography>
            {renderFieldGrid(groupFields, true)}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(FieldSection);
